'use client';

import { useState } from 'react';

import { SendLeadResponse } from '@/app/api/leads/utils';
import { useRouter } from '@/hooks/useRouter';
import { useToastError } from '@/hooks/useToastError';
import { formNames } from '@/lib/forms/types';
import {
  LandlordApplicationLeadDto,
  LocationApplicationLeadDto,
  MeetingEventSpaceInquiryLeadDto,
} from '@/lib/leads';
import { LocationPhases, SimpleLocationDto } from '@/lib/locations/dto';
import { getAppropiateCalendlyLink } from '@/utils/calendly';
import * as gtm from '@/utils/gtm';
import { HttpMethods, tryFetchToJson } from '@/utils/http-request';
import { stringifyUrl } from '@/utils/stringify-url';

type LeadDto =
  | LandlordApplicationLeadDto
  | MeetingEventSpaceInquiryLeadDto
  | LocationApplicationLeadDto;

export function useLeadCreation(location?: SimpleLocationDto) {
  const { showToast, hideToast } = useToastError();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isCalendlyDisplayed, setIsCalendlyDisplayed] = useState(false);

  const handleFormSubmitted = async (lead: LeadDto) => {
    setIsLoading(true);

    return tryFetchToJson<SendLeadResponse>({
      fetchParams: {
        input: getApiUrl(lead.formName),
        init: {
          method: HttpMethods.Post,
          body: JSON.stringify(lead),
        },
      },
      onSuccess: (response) => onLeadCreationSuccess(response, lead),
      onFunctionalError: onLeadCreationError,
      onTechnicalError: onLeadCreationError,
    });
  };

  const onLeadCreationError = () => {
    setIsLoading(false);
    showToast();
  };

  const onLeadCreationSuccess = (response: SendLeadResponse, lead: LeadDto) => {
    setIsLoading(false);
    hideToast();

    if (response.isFlaggedAsBot) {
      goToThankYouPage(lead);
      return;
    }

    gtm.pushEvent('formSubmissionSuccess');

    if (
      lead.formName === formNames.LOCATION_APPLICATION &&
      location &&
      location.phase !== LocationPhases.Signed &&
      getAppropiateCalendlyLink(location)
    ) {
      setIsCalendlyDisplayed(true);
      return;
    }

    gtm.pushEvent('form_process_complete', {
      form_type: lead.formName,
      customer_email: lead.email,
      customer_first_name: lead.firstName,
      customer_last_name: lead.lastName,
    });
    goToThankYouPage(lead);
  };

  const goToThankYouPage = ({ firstName, email }: LeadDto) => {
    const url = stringifyUrl('/contact-us/thank-you', {
      email,
      firstName,
      locationCountry: location?.country,
      locationPhase: location?.phase,
      locationPhone: location?.phone || undefined,
    });

    router.push(url);
  };

  const handleCalendlyModalSubmitted = (lead: LeadDto) => {
    setIsCalendlyDisplayed(false);

    gtm.pushEvent('form_process_complete', {
      form_type: lead.formName,
      customer_email: lead.email,
      customer_first_name: lead.firstName,
      customer_last_name: lead.lastName,
    });

    goToThankYouPage(lead);
  };

  return {
    isLoading,
    isCalendlyDisplayed,
    handleFormSubmitted,
    handleCalendlyModalSubmitted,
  };
}

function getApiUrl(formName: formNames) {
  const baseUrl = '/api/leads/';

  if (formName === formNames.LANDLORD) {
    return `${baseUrl}landlords`;
  }
  if (formName === formNames.MEETING_SPACE_REQUEST) {
    return `${baseUrl}meeting-event-space-inquiry`;
  }
  return `${baseUrl}locations`;
}
